import {
  FC,
  forwardRef,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  BLUR_COMMAND,
  COMMAND_PRIORITY_LOW,
  EditorState,
  EditorThemeClasses,
  FOCUS_COMMAND,
  FORMAT_TEXT_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  LexicalEditor,
} from "lexical";
import { $findMatchingParent, $getNearestNodeOfType } from "@lexical/utils";
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
} from "@lexical/list";
import { $setBlocksType } from "@lexical/selection";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  ORDERED_LIST,
  STRIKETHROUGH,
  TextFormatTransformer,
  UNORDERED_LIST,
} from "@lexical/markdown";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { logDebug, logError } from "@/utils/log";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { classNames } from "@/utils/classNames";
import { VBox } from "@/components-v2/Box";
import { DropdownMenu, DropdownMenuItems } from "@/components-v2/Dropdown";
import { Icon, IconProps } from "@/components-v2/Icon";
import { IconName } from "@/components-v2/iconMapping";
import { renderIcon } from "@/components-v2/iconHelpers";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { useTranslation } from "@/stores/RootStoreContext";
import { useElementId } from "@/utils/reactHelpers";
import { $createDCVariableNode, DCVariableNode } from "./DCVariableNode";
import { TMessage } from "@/i18n/tt";
import { DCVariableTransformer } from "./DCVariableTransformer";
import { last } from "lodash";
import { DCVariableSlashCommandPlugin } from "./DCVariableSlashCommandPlugin";
import { isDevEnvironment } from "@/utils/environments";
import { DataRequest } from "@/ai/autotext";

const ToolbarButton = forwardRef<
  HTMLButtonElement,
  {
    tooltip: string;
    isSelected?: boolean;
    isDisabled?: boolean;
  } & HTMLAttributes<HTMLButtonElement>
>(({ tooltip, isSelected, isDisabled, children, ...props }, ref) => {
  return (
    <button
      ref={ref}
      type={"button"}
      className={classNames(
        "inline-flex",
        "items-center",
        "leading-none",
        "gap-1",
        "p-2",
        "border-2",
        "rounded-sm",
        isDisabled && "opacity-50",
        !isDisabled && !isSelected && ["hover:bg-gray-x-soft", "hover:border-light-grey"],
        isSelected ? ["bg-gray", "border-gray"] : ["border-transparent"],
      )}
      tabIndex={-1}
      disabled={isDisabled}
      aria-pressed={isSelected}
      aria-label={tooltip}
      data-tooltip={tooltip}
      {...props}
    >
      {children}
    </button>
  );
});

export type ToolbarDropdown = {
  icon: IconName | IconProps;
  tooltip: TMessage;
  label?: TMessage;
  items: DropdownMenuItems;
  filter?: string;
  filterPlaceholder?: TMessage;
  onFilterChange?: (input: string) => void;
};

const Toolbar: FC<{
  isDisabled?: boolean;
  customOptions?: ToolbarDropdown[];
}> = ({ isDisabled, customOptions }) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [blockType, setBlockType] = useState<string | undefined>();

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsSuperscript(selection.hasFormat("superscript"));
      setIsSubscript(selection.hasFormat("subscript"));
      setIsUnderline(selection.hasFormat("underline"));

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
          const type = parentList ? parentList.getListType() : element.getListType();
          setBlockType(type);
        } else {
          const type = element.getType();
          setBlockType(type);
        }
      }
    }
  }, [editor]);

  const formatParagraph = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  }, [editor]);

  const formatBold = useCallback(
    () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold"),
    [editor],
  );

  const formatItalic = useCallback(
    () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic"),
    [editor],
  );

  const formatSuperscript = useCallback(
    () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "superscript"),
    [editor],
  );

  const formatSubscript = useCallback(
    () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "subscript"),
    [editor],
  );

  const formatUnderline = useCallback(
    () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline"),
    [editor],
  );

  const formatUnorderedList = useCallback(() => {
    if (blockType === "bullet") {
      formatParagraph();
    } else {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    }
  }, [editor, blockType, formatParagraph]);

  const formatOrderedList = useCallback(() => {
    if (blockType === "number") {
      formatParagraph();
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  }, [editor, blockType, formatParagraph]);

  useEffect(
    () =>
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(updateToolbar);
      }),
    [editor, updateToolbar],
  );

  return (
    <div className={"flex p-1 border-b border-gray justify-between"}>
      <div className={"gap-1"}>
        <ToolbarButton
          tooltip={t("richTextEditor.bold")}
          isSelected={isBold}
          onClick={formatBold}
          isDisabled={isDisabled}
        >
          <Icon name={"text-bolder"} />
        </ToolbarButton>
        <ToolbarButton
          tooltip={t("richTextEditor.italic")}
          isSelected={isItalic}
          onClick={formatItalic}
          isDisabled={isDisabled}
        >
          <Icon name={"text-italic"} />
        </ToolbarButton>
        <ToolbarButton
          tooltip={t("richTextEditor.underline")}
          isSelected={isUnderline}
          onClick={formatUnderline}
          isDisabled={isDisabled}
        >
          <Icon name={"text-underline"} />
        </ToolbarButton>
        <ToolbarButton
          tooltip={t("richTextEditor.superscript")}
          isSelected={isSuperscript}
          onClick={formatSuperscript}
          isDisabled={isDisabled}
        >
          <Icon name={"text-superscript"} />
        </ToolbarButton>
        <ToolbarButton
          tooltip={t("richTextEditor.subscript")}
          isSelected={isSubscript}
          onClick={formatSubscript}
          isDisabled={isDisabled}
        >
          <Icon name={"text-subscript"} />
        </ToolbarButton>
        <ToolbarButton
          tooltip={t("richTextEditor.bulletList")}
          isSelected={blockType === "bullet"}
          onClick={formatUnorderedList}
          isDisabled={isDisabled}
        >
          <Icon name={"list-bullets"} />
        </ToolbarButton>
        <ToolbarButton
          tooltip={t("richTextEditor.numberedList")}
          isSelected={blockType === "number"}
          onClick={formatOrderedList}
          isDisabled={isDisabled}
        >
          <Icon name={"list-numbers"} />
        </ToolbarButton>
      </div>
      <div className={"gap-1"}>
        {customOptions?.map((c) => (
          <DropdownMenu
            key={t(c.tooltip)}
            button={({ props }) => (
              <ToolbarButton tooltip={t(c.tooltip)} isDisabled={isDisabled} {...props}>
                {renderIcon(c.icon)}
                {c.label && <div>{t(c.label)}</div>}
              </ToolbarButton>
            )}
            items={c.items}
            alignment={"start"}
            gapAboveLabels
            filter={c.filter}
            filterPlaceholder={c.filterPlaceholder}
            onFilterChange={c.onFilterChange}
          />
        ))}
      </div>
    </div>
  );
};

const FocusPlugin: FC<{
  onFocus?: (editorState: EditorState, event: FocusEvent) => void;
  onBlur?: (editorState: EditorState, event: FocusEvent) => void;
}> = ({ onFocus, onBlur }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(
    () =>
      editor.registerCommand(
        BLUR_COMMAND,
        (event: FocusEvent) => {
          onBlur?.(editor.getEditorState(), event);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor, onBlur],
  );

  useEffect(
    () =>
      editor.registerCommand(
        FOCUS_COMMAND,
        (event: FocusEvent) => {
          onFocus?.(editor.getEditorState(), event);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor, onFocus],
  );

  return null;
};

const ReadOnlyController: FC<{ readOnly?: boolean }> = ({ readOnly }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  return null;
};

const changeNotificationDelay = 10_000;

const ChangePlugin = ({
  editorId,
  value,
  onChange,
  onInput,
}: {
  editorId: string;
  value: string;
  onChange: (value: string) => void;
  onInput?: () => void;
}) => {
  const [editor] = useLexicalComposerContext();
  const savePoint = useRef<string>();
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>();
  const focused = useRef(false);

  const clearDelayedChangeNotification = () => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
  };

  const notifyChanged = useCallback(() => {
    editor.getEditorState().read(() => {
      const v = $convertToMarkdownString(transformers);
      savePoint.current = v;
      if (v.trim() !== value.trim()) {
        logDebug("MarkdownEditor::ChangePlugin: notifying change", editorId);
        onChange(v);
      }
    });
  }, [editor, editorId, onChange, value]);

  const handleDelayedChange = useCallback(() => {
    clearDelayedChangeNotification();
    timeoutId.current = setTimeout(() => {
      notifyChanged();
    }, changeNotificationDelay);
  }, [notifyChanged]);

  const handleChange = useCallback(
    (_editorState: EditorState, _editor: LexicalEditor, tags: Set<string>) => {
      if (!tags.has("re-init")) {
        onInput?.();
        handleDelayedChange();
      }
    },
    [onInput, handleDelayedChange],
  );

  const handleFocus = useCallback(() => {
    logDebug("MarkdownEditor::ChangePlugin: focused", editorId);
    focused.current = true;
  }, [editorId]);

  const handleBlur = useCallback(() => {
    logDebug("MarkdownEditor::ChangePlugin: blurred", editorId);
    focused.current = false;
    clearDelayedChangeNotification();
    notifyChanged();
  }, [editorId, notifyChanged]);

  useEffect(() => {
    if (savePoint.current === undefined) {
      savePoint.current = value;
    } else if (value.trim() !== savePoint.current?.trim()) {
      logDebug("MarkdownEditor: detected external change, re-initializing...", {
        lastNotifiedValue: savePoint.current,
        incomingValue: value,
        editorId,
      });
      editor.update(
        () => {
          $convertFromMarkdownString(value, transformers);
          savePoint.current = value;
          if (!focused.current) {
            // prevent stealing focus if the editor wasn't already focused
            $setSelection(null);
          }
        },
        { tag: "re-init" },
      );
    }
  }, [editor, editorId, value]);

  useEffect(() => {
    logDebug("MarkdownEditor::ChangePlugin: mounted", editorId);
    return () => {
      logDebug("MarkdownEditor::ChangePlugin: unmounted", editorId);
      clearDelayedChangeNotification();
    };
  }, [editorId]);

  return (
    <>
      <OnChangePlugin onChange={handleChange} ignoreHistoryMergeTagChange ignoreSelectionChange />
      <FocusPlugin onFocus={handleFocus} onBlur={handleBlur} />
    </>
  );
};

const DCVariableDeletePlugin: FC = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      () => {
        let handled = false;
        editor.update(() => {
          const selection = $getSelection();
          if (
            $isNodeSelection(selection) &&
            selection.isCollapsed() &&
            last(selection.getNodes()) instanceof DCVariableNode
          ) {
            last(selection.getNodes())!.remove();
            handled = true;
            return;
          }
        });
        return handled;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_DELETE_COMMAND,
      () => {
        let handled = false;
        editor.update(() => {
          const selection = $getSelection();
          if (
            $isNodeSelection(selection) &&
            selection.isCollapsed() &&
            selection.getNodes()[0] instanceof DCVariableNode
          ) {
            selection.getNodes()[0]!.remove();
            handled = true;
            return;
          }
        });
        return handled;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor]);

  return null;
};

const MarkdownEditorApiPlugin = ({
  editorRef,
}: {
  editorRef?: (editorApi: MarkdownEditorApi) => void;
}) => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editorRef?.({
      insertVariable: (dataRequest) => {
        editor.update(() => {
          const selection = $getSelection();
          selection?.insertNodes([$createDCVariableNode(dataRequest), $createTextNode(" ")]);
        });
      },

      focus: () => {
        editor.focus();
      },
    });
  }, [editor, editorRef]);

  return null;
};

const SUPERSCRIPT: TextFormatTransformer = {
  format: ["superscript"],
  tag: "^",
  type: "text-format",
};

const SUBSCRIPT: TextFormatTransformer = {
  format: ["subscript"],
  tag: "~",
  type: "text-format",
};

const UNDERLINE: TextFormatTransformer = {
  format: ["underline"],
  tag: "++",
  type: "text-format",
};

const transformers = [
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  STRIKETHROUGH,
  SUPERSCRIPT,
  SUBSCRIPT,
  UNDERLINE,
  UNORDERED_LIST,
  ORDERED_LIST,
  DCVariableTransformer,
];

const nodes = [HorizontalRuleNode, ListNode, ListItemNode, DCVariableNode];

export type MarkdownEditorApi = {
  insertVariable: (dataRequest: DataRequest) => void;
  focus: () => void;
};

type MarkdownEditorProps = {
  label?: string | ReactNode;
  labelId?: string;
  placeholder?: string;
  value: string;
  /** Is fired whenever the user types inside the editor, just to let you know that the user is making changes */
  onInput?: () => void;
  /** Is fired either when the component loses focus, or when the user has been idle for some specific time (see `idleTimeout`) */
  onChange: (value: string) => void;
  /** The number of milliseconds of the user being idle before a change should be notified, defaults to 5000 (5s) */
  idleTimeout?: number;
  testId?: string;
  readOnly?: boolean;
  customOptions?: ToolbarDropdown[];
  size?: "auto" | "full";
  disableMinHeight?: boolean;
  autofocus?: boolean;
  editorRef?: (editor: MarkdownEditorApi) => void;
  includeSlashCommand?: boolean;
  withoutToolbar?: boolean;
  undecorated?: boolean;
};

let currentEditorId = 1;

const useEditorId = () => useMemo(() => `editor-${currentEditorId++}`, []);

export function MarkdownEditor({
  label,
  placeholder,
  value,
  onInput,
  onChange,
  testId,
  readOnly,
  customOptions,
  size = "auto",
  autofocus,
  editorRef,
  includeSlashCommand = false,
  withoutToolbar,
  undecorated,
  disableMinHeight,
  ...props
}: MarkdownEditorProps) {
  const editorId = useEditorId();
  const labelId = useElementId();
  const theme: EditorThemeClasses = {
    text: {
      underline: "underline",
    },
  };

  const handleClick = useCallback(() => {
    document.getElementById(editorId)?.focus();
  }, [editorId]);

  const handleError = useCallback((err: Error) => {
    logError(err, "Error in MarkdownEditor");
  }, []);

  return (
    <VBox
      data-test-id={testId}
      htmlProps={{ onClick: handleClick }}
      className={classNames("grow", size === "full" && ["h-full", "w-full"])}
    >
      {label && <label id={props.labelId ?? labelId}>{label}</label>}
      <div
        className={classNames(
          "relative",
          "flex",
          "flex-col",
          "grow",
          !undecorated && [
            "border",
            "border-gray",
            "rounded-sm",
            "focus-within:ring-1",
            "focus-within:border-primary",
            "focus-within:ring-primary",
            "hover:border-gray-hard",
            "hover:focus-within:border-primary",
          ],
          "color-default",
          "bg-default",
          size === "auto" && !disableMinHeight && "min-h-40",
          size === "full" && "overflow-hidden",
        )}
      >
        <LexicalComposer
          initialConfig={{
            editorState: value ? () => $convertFromMarkdownString(value, transformers) : undefined,
            namespace: "MarkdownEditor",
            onError: handleError,
            theme: theme,
            nodes: nodes,
            editable: !readOnly,
          }}
        >
          <MarkdownEditorApiPlugin editorRef={editorRef} />
          <MarkdownShortcutPlugin transformers={transformers} />
          {!withoutToolbar && <Toolbar isDisabled={readOnly} customOptions={customOptions} />}
          <RichTextPlugin
            contentEditable={
              <div className={classNames("grow", size === "full" && "overflow-auto")}>
                <ContentEditable
                  className={classNames(
                    "outline-hidden",
                    "content",
                    !undecorated && ["px-4", "mt-4"],
                  )}
                  ariaLabelledBy={props.labelId ?? labelId}
                  id={editorId}
                />
              </div>
            }
            placeholder={
              <div
                className={classNames(
                  "absolute",
                  "pointer-events-none",
                  "opacity-50",
                  !undecorated && ["top-16", "left-4"],
                )}
              >
                {placeholder ?? ""}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <ListPlugin />
          <HistoryPlugin />
          <ReadOnlyController readOnly={readOnly} />
          <DCVariableDeletePlugin />
          <ChangePlugin value={value} onChange={onChange} onInput={onInput} editorId={editorId} />
          {autofocus && <AutoFocusPlugin />}
          {isDevEnvironment && !readOnly && includeSlashCommand && <DCVariableSlashCommandPlugin />}
        </LexicalComposer>
      </div>
    </VBox>
  );
}
